import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useCornTechnologies } from '/@/hooks/catalog';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { useForecastNotifications } from '/@/hooks/weather';
import { _ } from '/@/locale';
import { useGetRecommendationsFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { formatDate } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import { reverse } from '/@/utils/router';
import ScheduleCard from '../../bayer/schedule-card';
import HarvestPreparationCard from '../harvest-preparation-card';
import { getActiveStep } from '../progress-bar/assets/BASE_STEPS';

const DateSensitiveCard = ({
    field,
    cropMonitoring,
    setCMFormModalIsOpen,
}: {
    field: Field;
    cropMonitoring?: CropMonitoring;
    setCMFormModalIsOpen: (bool: boolean) => void;
}) => {
    const month = new Date().getMonth();
    const navigate = useNavigate();
    const isBayer = useIsValidatedBayerUserType();
    const ownUserNodeQuery = useOwnUserNode();

    const recommendationsQuery = useGetRecommendationsFromAPI(
        {
            page: 1,
            filters: {
                field_name: field?.name || '',
                type: 'corn',
            },
            ordering: '-created',
            pageSize: 100000,
        },
        {
            revalidateOnMount: true,
        }
    );

    const hasACropRecommendation = useMemo(
        () =>
            (recommendationsQuery.data?.results?.data || []).some((reco) => {
                return (
                    reco?.field === field.id &&
                    new Date(reco.crops?.[0]?.plantingDate).getFullYear() === new Date().getFullYear()
                );
            }),
        [recommendationsQuery.data, field.id]
    );

    if (!cropMonitoring || month >= 6) {
        if (month < 3)
            // Enero a Marzo
            return (
                <ScheduleCard
                    title={_('¡Aprovechá el seguimiento de tu lote!')}
                    description={_(
                        'Te ayudamos a seguir cada etapa del ciclo de tu cultivo y acceder a recomendaciones personalizadas para optimizar su manejo.'
                    )}
                    actionText={_('Agregar cultivo')}
                    actionOnClick={() => setCMFormModalIsOpen(true)} // Agregar Cultivo
                />
            );
        if (month < 6)
            // Marzo a Junio
            return hasACropRecommendation ? (
                <ScheduleCard
                    title={_('¡Ya podés acceder a tus recomendaciones!')}
                    description={_(
                        'Tu lote ya posee recomendaciones específicas para cada etapa del ciclo del cultivo. Ingresá a "Mis Recomendaciones" para descubrir las mejores prácticas en el manejo de tu lote.'
                    )}
                    actionText={_('Ir a "Mis Recomendaciones"')}
                    actionOnClick={() => {
                        navigate(
                            isBayer
                                ? field.userNode === ownUserNodeQuery.data?.id
                                    ? reverse('MyFields.list', {
                                          qs: { tab: 'recommendations' },
                                      })
                                    : reverse('Growers.detail', {
                                          params: {
                                              growerNodeId: field.userNode || '',
                                          },
                                          qs: {
                                              tab: 'recommendations',
                                              fieldId: field.id,
                                          },
                                      })
                                : reverse('recommendations.List', {
                                      qs: {
                                          fieldId: field.id,
                                      },
                                  })
                        );
                    }}
                />
            ) : (
                <ScheduleCard
                    title={_('Planificá tu próxima campaña')}
                    description={_(
                        'Obtené una recomendación personalizada para tu lote con el híbrido ideal para las condiciones específicas de su ambiente y optimizá el rendimiento desde el comienzo.'
                    )}
                    actionText={_('Obtené tu recomendación')}
                    actionOnClick={() => {
                        navigate(
                            reverse('recommendations.RecommendationEdit', {
                                params: {
                                    fieldId: field.id,
                                    crop: 'corn',
                                },
                                qs: { entrySource: 'crop-monitoring:action-card' },
                            })
                        );
                    }} // Reco Maiz
                />
            );
    }

    // Junio a Diciembre or if doesn't has a Crop Monitoring
    return (
        <ScheduleCard
            title={_('Es momento de revisar el manejo de malezas')}
            description={_('Recibí recomendaciones personalizadas para el control eficiente de malezas en tu lote.')}
            actionText={_('Obtené tu recomendación')}
            actionOnClick={() => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'crop-monitoring-stage-alert',
                    crop: 'corn',
                    cp_type: 'weed',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: field.id,
                            crop: 'corn',
                            type: 'weeds',
                            plantingDate: cropMonitoring?.plantingDate,
                            userNodeId: field.userNode,
                        },
                    })
                );
            }}
        />
    );
};

const WatchYourCrops = ({ cropMonitoring }: { cropMonitoring: CropMonitoring }) => {
    const navigate = useNavigate();
    return (
        <ScheduleCard
            title={_('Controlá plagas y enfermedades')}
            description={_(
                'Actuá a tiempo y vigilá la presencia de plagas y enfermedades en tu lote. Protegé tu cultivo hoy.'
            )}
            actionText={_('Obtené tu recomendación')}
            actionOnClick={() => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'crop-monitoring-stage-alert',
                    crop: 'corn',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: cropMonitoring.id,
                            crop: 'corn',
                            plantingDate: cropMonitoring.plantingDate,
                            userNodeId: cropMonitoring.userNode,
                        },
                    })
                );
            }}
        />
    );
};
const CropsAreEmerging = ({ cropMonitoring }: { cropMonitoring: CropMonitoring }) => {
    const navigate = useNavigate();
    return (
        <ScheduleCard
            title={_('Tus cultivos están emergiendo')}
            description={_('Recibí tu recomendación personalizada y comenzá la campaña con el lote libre de malezas.')}
            actionText={_('Obtené tu recomendación')}
            actionOnClick={() => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'crop-monitoring-stage-alert',
                    crop: 'corn',
                    cp_type: 'weed',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: cropMonitoring.field,
                            crop: 'corn',
                            type: 'weeds',
                            plantingDate: cropMonitoring.plantingDate,
                            userNodeId: cropMonitoring.userNode,
                        },
                    })
                );
            }}
        />
    );
};

const NSmartUnconfirmedPlantingDate = ({ NSmartRecommendationId }: { NSmartRecommendationId: number }) => {
    const navigate = useNavigate();
    return (
        <ScheduleCard
            title={_('Es momento de fertilizar')}
            description={_(
                'Solicitaste la recomendación de Nitrógeno. Ahora necesitamos que confirmes la fecha de tu re-fertilización para procesar los datos y enviarte una recomendación personalizada.'
            )}
            actionText={_('Confirmar fecha')}
            actionOnClick={() => {
                navigate(
                    reverse('nitrogenRecommendation.NitrogenRecommendationDetail', {
                        params: {
                            id: NSmartRecommendationId,
                        },
                    })
                );
            }}
        />
    );
};

const CpRecommendationCard = ({ cropMonitoring }: { cropMonitoring: CropMonitoring }) => {
    const navigate = useNavigate();
    return (
        <ScheduleCard
            title={_('Protegé tu cultivo')}
            description={_(
                'Es el momento clave para prevenir enfermedades y plagas que puedan comprometer tu lote. Obtené una recomendación para proteger tu cultivo.'
            )}
            actionText={_('Obtené tu recomendación')}
            actionOnClick={() => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'crop-monitoring-stage-alert',
                    crop: 'corn',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: cropMonitoring.field,
                            crop: 'corn',
                            plantingDate: cropMonitoring.plantingDate,
                            userNodeId: cropMonitoring.userNode,
                        },
                    })
                );
            }}
        />
    );
};

const CpFungicideRecommendationCard = ({
    cropMonitoring,
    stageLabel,
}: {
    cropMonitoring: CropMonitoring;
    stageLabel: string;
}) => {
    const navigate = useNavigate();

    return (
        <ScheduleCard
            title={_('Aprovechá la ventana de aplicación de fungicida')}
            description={_(
                'Tu cultivo está en la etapa {{stageLabel}}, es el mejor momento para aplicar fungicidas y proteger tu cultivo del estrés y las enfermedades.',
                {
                    stageLabel,
                }
            )}
            actionText={_('Obtené tu recomendación')}
            actionOnClick={() => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'crop-monitoring-stage-alert',
                    crop: 'corn',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: cropMonitoring.field,
                            crop: 'corn',
                            plantingDate: cropMonitoring.plantingDate,
                            userNodeId: cropMonitoring.userNode,
                        },
                    })
                );
            }}
        />
    );
};

const CriticPeriodCard = ({
    expectedFloweringDate,
    notifications,
}: {
    expectedFloweringDate?: string;
    notifications: string[];
}) => {
    if (!expectedFloweringDate) return null;

    const today = new Date();
    const formattedExpectedFloweringDate = new Date(expectedFloweringDate);

    const startCriticPeriod = new Date(formattedExpectedFloweringDate);
    startCriticPeriod.setDate(startCriticPeriod.getDate() - 15);

    const endCriticPeriod = new Date(formattedExpectedFloweringDate);
    endCriticPeriod.setDate(endCriticPeriod.getDate() + 15);

    const startCriticPeriodNotification = new Date(startCriticPeriod);
    startCriticPeriodNotification.setDate(startCriticPeriodNotification.getDate() - 5);

    const endCriticPeriodNotification = new Date(endCriticPeriod);
    endCriticPeriodNotification.setDate(endCriticPeriodNotification.getDate() - 5);

    if (today >= startCriticPeriodNotification && today < startCriticPeriod)
        return (
            <ScheduleCard
                title={_('El período crítico esta por empezar')}
                description={
                    _(
                        'Tu cultivo está entrando en su etapa más sensible, donde se define el rendimiento final. Es fundamental monitorear plagas, enfermedades y las condiciones climáticas.'
                    ) +
                    (isNaN(formattedExpectedFloweringDate.getTime())
                        ? ' ' +
                          _('Fecha estimada de floración {{expectedFloweringDate}}.', {
                              expectedFloweringDate: formatDate(formattedExpectedFloweringDate),
                          })
                        : '')
                }
                alerts={notifications}
            />
        );
    if (today >= startCriticPeriod && today < endCriticPeriodNotification)
        return (
            <ScheduleCard
                title={_('Período crítico en curso')}
                description={
                    _(
                        'El cultivo atraviesa su etapa más sensible donde se define el rendimiento final. Es fundamental monitorear plagas, enfermedades y las condiciones climáticas.'
                    ) +
                    (isNaN(formattedExpectedFloweringDate.getTime())
                        ? ' ' +
                          _('Fecha estimada de floración {{expectedFloweringDate}}.', {
                              expectedFloweringDate: formatDate(formattedExpectedFloweringDate),
                          })
                        : '')
                }
                alerts={notifications}
            />
        );
    if (today >= endCriticPeriodNotification && today < endCriticPeriod)
        return (
            <ScheduleCard
                title={_('El período crítico está por finalizar')}
                description={
                    _(
                        'Tu cultivo está por completar su etapa más sensible. Ahora es clave continuar monitoreando su desarrollo y planificar los proximos pasos para la cosecha.'
                    ) +
                    (isNaN(formattedExpectedFloweringDate.getTime())
                        ? ' ' +
                          _('Fecha estimada de floración {{expectedFloweringDate}}.', {
                              expectedFloweringDate: formatDate(formattedExpectedFloweringDate),
                          })
                        : '')
                }
                alerts={notifications}
            />
        );

    return null;
};

const StageActionCard = ({
    cropMonitoring,
    field,
    setYieldEstimationDrawerIsOpen,
    setCMFormModalIsOpen,
    showHarvestRequest,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
    setYieldEstimationDrawerIsOpen: (bool: boolean) => void;
    setCMFormModalIsOpen: (bool: boolean) => void;
    showHarvestRequest: boolean;
}) => {
    const cropData = getCropMonitoringData(cropMonitoring);
    const activeStep = getActiveStep((cropData?.stages || []).at(-1));
    const navigate = useNavigate();

    const NSmartRecommendation = cropMonitoring?.nitrogenRecomendations?.[0];

    const technologiesQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const cropHasVTPRO4 = useMemo(
        () => (technologiesQuery.data?.results || []).some((tech) => tech.name === 'VTPRO4'),
        [technologiesQuery.data?.results]
    );

    const notifications = useForecastNotifications({ ...field });

    const CriticalPeriodAndCpFungicideRecommendationCardElement = useMemo(() => {
        if (!cropMonitoring || !activeStep) return <></>;
        const CriticalPeriodElement = CriticPeriodCard({
            expectedFloweringDate: cropData?.expectedFloweringDate,
            notifications: notifications,
        });
        return (
            CriticalPeriodElement ?? (
                <CpFungicideRecommendationCard cropMonitoring={cropMonitoring} stageLabel={activeStep.displayName!} />
            )
        );
    }, [activeStep, cropMonitoring, notifications]);

    const CriticalPeriodAndFloweringCardElement = useMemo(() => {
        if (!cropMonitoring) return <></>;
        const CriticalPeriodElement = CriticPeriodCard({
            expectedFloweringDate: cropData?.expectedFloweringDate,
            notifications: notifications,
        });
        return (
            CriticalPeriodElement ?? (
                <ScheduleCard
                    title={_('Tu cultivo esta en floración.')}
                    description={
                        _(
                            'Continuá monitoreando el desarrollo de tu cultivo y planificar los proximos pasos para la cosecha.'
                        ) +
                        (cropData?.expectedMaturityDate
                            ? ' ' +
                              _('Fecha estimada de madurez fisiologica {{expectedMaturityDate}}.', {
                                  expectedMaturityDate: formatDate(new Date(cropData?.expectedMaturityDate)),
                              })
                            : '')
                    }
                    alerts={notifications}
                />
            )
        );
    }, [cropMonitoring, notifications]);

    const plantingDateIs1MonthAhead = useMemo(() => {
        if (!cropMonitoring) return false;
        const plantingDate = new Date(cropMonitoring.plantingDate);
        const today = new Date();
        const oneMonthAhead = new Date(today);
        oneMonthAhead.setMonth(oneMonthAhead.getMonth() + 1);
        return plantingDate < oneMonthAhead;
    }, [cropMonitoring]);

    const cardByState = {
        PRE: plantingDateIs1MonthAhead ? (
            <ScheduleCard
                title={_('¡Se acerca la fecha de siembra!')}
                description={_(
                    'Recibí tu recomendación personalizada y comenzá la campaña con el lote libre de malezas.'
                )}
                actionText={_('Obtené tu recomendación')}
                actionOnClick={() => {
                    mpTrackEvent('Crop Protection Recommendation began', {
                        entry_source: 'crop-monitoring-stage-alert',
                        crop: 'corn',
                        cp_type: 'weed',
                    });
                    navigate(
                        reverse('cropProtectionRecommendation', {
                            qs: {
                                fieldId: cropMonitoring!.field,
                                crop: 'corn',
                                type: 'weeds',
                                plantingDate: cropMonitoring?.plantingDate,
                                userNodeId: cropMonitoring?.userNode,
                            },
                        })
                    );
                }}
            />
        ) : (
            <DateSensitiveCard
                cropMonitoring={cropMonitoring}
                field={field}
                setCMFormModalIsOpen={setCMFormModalIsOpen}
            />
        ),
        SE: cropMonitoring ? (
            <ScheduleCard
                title={_('Cuidá tu cultivo desde el comienzo')}
                description={_(
                    'Recibí tu recomendación personalizada y comenzá la campaña con el lote libre de malezas.'
                )}
                actionText={_('Obtené tu recomendación')}
                actionOnClick={() => {
                    mpTrackEvent('Crop Protection Recommendation began', {
                        entry_source: 'crop-monitoring-stage-alert',
                        crop: 'corn',
                        cp_type: 'weed',
                    });
                    navigate(
                        reverse('cropProtectionRecommendation', {
                            qs: {
                                fieldId: cropMonitoring.field,
                                crop: 'corn',
                                type: 'weeds',
                                plantingDate: cropMonitoring.plantingDate,
                                userNodeId: cropMonitoring.userNode,
                            },
                        })
                    );
                }}
            />
        ) : (
            <DateSensitiveCard
                cropMonitoring={cropMonitoring}
                field={field}
                setCMFormModalIsOpen={setCMFormModalIsOpen}
            />
        ),
        VE: <CropsAreEmerging cropMonitoring={cropMonitoring!} />,
        V1: <CropsAreEmerging cropMonitoring={cropMonitoring!} />,
        V2: NSmartRecommendation ? (
            <WatchYourCrops cropMonitoring={cropMonitoring!} />
        ) : (
            <ScheduleCard
                title={_('Se aproxima el momento ideal para fertilizar')}
                description={_(
                    'Obtené una recomendación personalizada de N-SMART (nitrógeno) para aplicar lo que tu cultivo necesita.'
                )}
                actionText={_('Pedí tu recomendación')}
                actionOnClick={() => {
                    mpTrackEvent('N-Smart Recommendation began', {
                        entry_source: 'crop-monitoring-stage-alert',
                    });
                    navigate(
                        reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                            qs: { cropMonitoringId: cropMonitoring?.id },
                        })
                    );
                }}
            />
        ),
        V3: <WatchYourCrops cropMonitoring={cropMonitoring!} />,
        V4: <WatchYourCrops cropMonitoring={cropMonitoring!} />,
        V5:
            NSmartRecommendation && !NSmartRecommendation?.applicationDate ? (
                <NSmartUnconfirmedPlantingDate NSmartRecommendationId={NSmartRecommendation.id!} />
            ) : (
                <CpRecommendationCard cropMonitoring={cropMonitoring!} />
            ),
        V6:
            NSmartRecommendation && !NSmartRecommendation?.applicationDate ? (
                <NSmartUnconfirmedPlantingDate NSmartRecommendationId={NSmartRecommendation.id!} />
            ) : (
                <ScheduleCard
                    title={_('Controlá plagas y enfermedades')}
                    description={_(
                        'Actuá a tiempo y vigilá la presencia de plagas y enfermedades en tu lote. Protegé tu cultivo hoy.'
                    )}
                    actionText={_('Obtené tu recomendación')}
                    actionOnClick={() => {
                        mpTrackEvent('Crop Protection Recommendation began', {
                            entry_source: 'crop-monitoring-stage-alert',
                            crop: 'corn',
                        });
                        navigate(
                            reverse('cropProtectionRecommendation', {
                                qs: {
                                    fieldId: cropMonitoring!.field,
                                    crop: 'corn',
                                    plantingDate: cropMonitoring!.plantingDate,
                                    userNodeId: cropMonitoring!.userNode,
                                },
                            })
                        );
                    }}
                    alerts={[
                        cropHasVTPRO4
                            ? _(
                                  'Tu híbrido tiene tecnología VTPRO4. Recordá que no podés aplicar productos a base de glifosato a partir de la etapa V6.'
                              )
                            : _(
                                  'Recordá que si el híbrido que sembraste tiene tecnología VTPRO4, no podés aplicar productos a base de glifosato a partir de V6.'
                              ),
                    ]}
                />
            ),
        V7: <CpRecommendationCard cropMonitoring={cropMonitoring!} />,
        V8: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V9: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V10: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V11: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V12: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V13: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V14: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V15: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V16: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V17: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V18: CriticalPeriodAndCpFungicideRecommendationCardElement,
        V19: CriticalPeriodAndCpFungicideRecommendationCardElement,
        R: CriticalPeriodAndFloweringCardElement,
        FM: (
            <HarvestPreparationCard
                showHarvestRequest={showHarvestRequest}
                cropMonitoring={cropMonitoring!}
                setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
            />
        ),
        H: <></>,
    };

    return cardByState[activeStep?.state || 'PRE'];
};

export default StageActionCard;
